import { useState, useEffect } from 'react';
import { UploadDocument } from '../types';

interface UseDocumentManagerProps {
  documents: UploadDocument[];
  category: string | null;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  deleteDocument?: (documentId: string) => void;
}

export const useDocumentManager = ({
  documents,
  category,
  handleFileChange,
  deleteDocument,
}: UseDocumentManagerProps) => {
  const [, setUploadProgress] = useState<Record<string, number>>({});
  const [localDocuments, setLocalDocuments] = useState<UploadDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Update local documents when props change
  useEffect(() => {
    setLocalDocuments((prevDocs) => {
      const tempDocs = prevDocs.filter((doc) =>
        doc.documentId?.startsWith('temp-'),
      );
      const serverDocs = documents || [];

      // Remove temporary documents that have been successfully uploaded
      const remainingTempDocs = tempDocs.filter((tempDoc) => {
        if (tempDoc.status !== 'completed') return true;

        const hasServerVersion = serverDocs.some(
          (serverDoc) => serverDoc.name === tempDoc.name,
        );

        return !hasServerVersion;
      });

      // Combine remaining temp docs with server docs, avoiding duplicates
      const allDocs = [...remainingTempDocs];

      serverDocs.forEach((serverDoc) => {
        const exists = allDocs.some(
          (doc) => doc.documentId === serverDoc.documentId,
        );
        if (!exists) {
          allDocs.push(serverDoc);
        }
      });

      return allDocs;
    });
  }, [documents]);

  const processFiles = async (files: File[]) => {
    if (files.length === 0) return;

    // Check if category is selected
    if (!category) {
      alert('Please select a category before uploading documents.');
      return;
    }

    setIsUploading(true);

    // Create temporary documents for immediate display
    const tempDocuments: UploadDocument[] = files.map((file) => ({
      documentId: `temp-${Date.now()}-${Math.random()}`,
      name: file.name,
      url: '',
      category: category || '',
      status: 'uploading',
      progress: 0,
      uploadedSize: 0,
      totalSize: file.size,
    }));

    // Add temporary documents to local state
    setLocalDocuments((prev) => [...prev, ...tempDocuments]);

    // Simulate upload progress for each file
    const progressIntervals: NodeJS.Timeout[] = [];

    tempDocuments.forEach((doc) => {
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          const docId = doc.documentId || '';
          const currentProgress = prev[docId] || 0;
          const newProgress = Math.min(
            currentProgress + Math.random() * 15,
            90,
          );

          setLocalDocuments((prevDocs) => {
            return prevDocs.map((d) => {
              if (d.documentId !== doc.documentId) return d;
              const totalBytes = d.totalSize ?? 0;
              const uploadedBytes = Math.round(
                (totalBytes * newProgress) / 100,
              );
              return {
                ...d,
                progress: newProgress,
                uploadedSize: uploadedBytes,
              };
            });
          });

          return { ...prev, [docId]: newProgress };
        });
      }, 200);
      progressIntervals.push(interval);
    });

    // Create the event object for the parent handler
    const event = {
      target: {
        files: files,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    try {
      await handleFileChange(event);

      // Mark temporary documents as completed
      setLocalDocuments((prevDocs) =>
        prevDocs.map((doc) => {
          if (
            !tempDocuments.some((temp) => temp.documentId === doc.documentId)
          ) {
            return doc;
          }
          const totalBytes = doc.totalSize ?? 0;
          return {
            ...doc,
            status: 'completed',
            progress: 100,
            uploadedSize: totalBytes,
          };
        }),
      );
    } catch (error) {
      console.error('File upload failed:', error);

      // Mark temporary documents as failed
      setLocalDocuments((prevDocs) =>
        prevDocs.map((doc) =>
          tempDocuments.some((temp) => temp.documentId === doc.documentId)
            ? { ...doc, status: 'failed' }
            : doc,
        ),
      );
    } finally {
      progressIntervals.forEach((interval) => clearInterval(interval));
      setIsUploading(false);
    }
  };

  const handleDrop = async (acceptedFiles: File[]) => {
    await processFiles(acceptedFiles);
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      await processFiles(fileArray);
    }

    // Clear the input value to allow re-uploading the same file
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDeleteDocument = (documentId: string) => {
    if (!deleteDocument) return;

    setLocalDocuments((prev) =>
      prev.filter((doc) => doc.documentId !== documentId),
    );
    deleteDocument(documentId);
  };

  return {
    localDocuments,
    isUploading,
    handleDrop,
    handleFileInputChange,
    handleDeleteDocument,
  };
};
